import { Callout } from "@bednbody/ui/components/callout";
// Overrides available from the opened booking, mirroring exactly what the
// server allows. The limits on each are rules with sources — not UI
// preferences.
//
// ═══ SESSION FIXES — moved here from the Bookings table (2026-09-18) ═══
//
// Start session (with an optional corrected start time — the Hub Manager "fix
// session start times" story, UX Notes §Hub Manager) and Mark completed used to
// be an Actions column on every table row, a second place to act on a booking
// beside this popup. They live here now, next to the data they change. Mark
// completed runs `complete_booking_service` server-side (booking.service.ts),
// so the pickup and cash chain start exactly as they do for a timer completion.
//
// ═══ CANCEL, AND "CLIENT ASKED TO STOP" — two different things mid-service ═══
//
// A staff CANCEL is a system cancel (Leon, 2026-09-29): no fee, everything the
// client paid returns as Credits, Gift Passes are restored (CORE-08 §4.7). That
// is the company stopping the booking — an emergency, an SOS.
//
// CLIENT ASKED TO STOP exists only while the Service runs (0276, Leon
// 2026-10-02): the client's own request, entered by Support. The service is
// paid — no refund — and the Therapist earns her normal commission; a cash
// booking still owes its full amount. It is the ONLY way a cash client's
// service is stopped early, because the client app offers them no cancel.
//
// ⚠ Pressing the wrong one mid-service is a money mistake in either direction,
// so each pane says plainly what it does to the client's money, and the Cancel
// pane points at the other button while a session is in progress.
//
// Hub Managers may do both for their own hub. All of it is enforced
// server-side; the gating here is so an operator is not offered a button that
// will refuse them.
//
// A REASON IS REQUIRED before either button enables. It goes to `audit_log`
// (booking.service.ts) — `booking_events` records that the machine moved, not
// that a person decided.
//
// ═══ TRANSFER RIDER — pre-pickup only, and hub managers CAN ═══
//
// `POST /dispatch/assign-rider` (0053 `admin_assign_rider`) admits
// hub_manager/admin/superadmin — a wider set than cancel, deliberately: moving
// a rider is an operational fix, not a commercial decision. It bumps the
// current holder back to available and applies the same hub/availability
// checks FIFO makes.
//
// Pre-pickup ONLY. 0053's header is explicit that once a live trip exists there
// is real GPS and mileage state, and that case belongs to the status-override
// path instead. So the control disappears from `en_route_to_client` onward
// rather than offering a transfer the function will reject.

import { useState } from "react";
import { Button } from "@bednbody/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bednbody/ui/components/card";
import { Input } from "@bednbody/ui/components/input";
import { Label } from "@bednbody/ui/components/label";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import { Textarea } from "@bednbody/ui/components/textarea";
import { useTransitionBooking } from "@/features/bookings/bookings.hooks";
import { useAssignSpecificRider, useRiderQueue } from "@/features/dispatch/dispatch.hooks";
import { useAuth } from "@/lib/auth";

/**
 * Cancellable per the transition matrix. `in_session` is here as of Leon's call
 * (2026-08-08) — mid-session cancel is hub-staff-only, enforced in
 * `assertActorAllowed`, not by this list.
 */
const CANCELLABLE = new Set([
  "pending",
  "awaiting_therapist",
  "awaiting_rider",
  "dispatched",
  "en_route_to_client",
  "arrived",
  "in_session",
]);

/** 0053 refuses once a trip is live; `dispatched` is the last pre-pickup state. */
const RIDER_TRANSFERABLE = new Set(["awaiting_rider", "dispatched"]);

const OVERRIDE_ROLES = ["hub_manager", "admin", "superadmin"];

interface Props {
  booking: {
    id: string;
    ref: string;
    status: string;
    hub_id?: string | null;
    rider_id?: string | null;
    gross_amount?: number;
    /** Null for a hub manager — no grant on it (0285). */
    amount_due: number | null;
    payment_method: string;
  };
  onDone: () => void;
}

type Pane = "none" | "cancel" | "client_stop" | "transfer" | "fix_start" | "complete";

export function BookingDetailActions({ booking, onDone }: Props) {
  const { claims } = useAuth();
  const [pane, setPane] = useState<Pane>("none");
  const [reason, setReason] = useState("");
  const [riderId, setRiderId] = useState("");
  const [correctedAt, setCorrectedAt] = useState("");

  const transition = useTransitionBooking();

  if (!claims) return null;
  const canOverride = OVERRIDE_ROLES.includes(claims.role);
  const canCancel = canOverride && CANCELLABLE.has(booking.status);
  const canTransferRider = canOverride && RIDER_TRANSFERABLE.has(booking.status);
  const canStartSession = canOverride && booking.status === "arrived";
  const canComplete = canOverride && booking.status === "in_session";
  // Only while the Service runs — before that, the client cancels for themselves.
  const canClientStop = canOverride && booking.status === "in_session";

  if (!canCancel && !canTransferRider && !canStartSession && !canComplete && !canClientStop) {
    return (
      <p className="rounded-xl border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
        No actions available for this booking.
      </p>
    );
  }

  const busy = transition.isPending;
  const error = transition.error as Error | null;

  const back = () => {
    setPane("none");
    transition.reset();
  };

  function runCancel() {
    transition.mutate(
      { bookingId: booking.id, to: "cancelled", reason: reason.trim() },
      {
        onSuccess: () => {
          setPane("none");
          setReason("");
          onDone();
        },
      },
    );
  }

  function runClientStop() {
    transition.mutate(
      { bookingId: booking.id, to: "cancelled", clientRequested: true, reason: reason.trim() },
      {
        onSuccess: () => {
          setPane("none");
          setReason("");
          onDone();
        },
      },
    );
  }

  function runStartSession() {
    transition.mutate(
      {
        bookingId: booking.id,
        to: "in_session",
        // datetime-local has no offset; toISOString converts the local value to UTC.
        correctedAt: correctedAt ? new Date(correctedAt).toISOString() : undefined,
      },
      {
        onSuccess: () => {
          setPane("none");
          setCorrectedAt("");
        },
      },
    );
  }

  function runComplete() {
    transition.mutate(
      { bookingId: booking.id, to: "completed" },
      { onSuccess: () => setPane("none") },
    );
  }

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle>Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {pane === "none" ? (
          <div className="flex flex-wrap gap-2">
            {canStartSession ? (
              <>
                <Button disabled={busy} onClick={runStartSession}>
                  {busy ? "Starting…" : "Start session"}
                </Button>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => setPane("fix_start")}
                  title="Record the real start time if the therapist forgot to tap."
                >
                  Fix start time…
                </Button>
              </>
            ) : null}
            {canComplete ? (
              <Button disabled={busy} onClick={() => setPane("complete")}>
                Mark completed
              </Button>
            ) : null}
            {canTransferRider ? (
              <Button variant="outline" onClick={() => setPane("transfer")}>
                Transfer rider
              </Button>
            ) : null}
            {canClientStop ? (
              <Button variant="outline" onClick={() => setPane("client_stop")}>
                Client asked to stop
              </Button>
            ) : null}
            {canCancel ? (
              <Button variant="destructive" onClick={() => setPane("cancel")}>
                Cancel booking
              </Button>
            ) : null}
          </div>
        ) : null}

        {pane === "client_stop" ? (
          <div className="space-y-2">
            <div className="rounded-lg bg-muted p-2.5 text-xs leading-relaxed">
              <p className="font-semibold">Stop {booking.ref} at the client&apos;s request?</p>
              <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-muted-foreground">
                <li>The service is paid. No refund.</li>
                <li>The therapist earns her normal commission.</li>
                {booking.payment_method === "cash" ? (
                  <li>
                    {booking.amount_due === null
                      ? "The full amount is still due in cash. She confirms it when she collects it."
                      : `The full ₱${booking.amount_due.toFixed(2)} is still due in cash. She confirms it when she collects it.`}
                  </li>
                ) : null}
              </ul>
            </div>
            <Label htmlFor="client-stop-reason">
              Reason <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="client-stop-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="What did the client say?"
            />
            <div className="flex gap-2">
              <Button disabled={reason.trim().length === 0 || busy} onClick={runClientStop}>
                {busy ? "Stopping…" : "Confirm: no refund"}
              </Button>
              <Button variant="outline" onClick={back}>
                Back
              </Button>
            </div>
          </div>
        ) : null}

        {pane === "fix_start" ? (
          <div className="space-y-2">
            <Label htmlFor="corrected-start">Actual session start</Label>
            <Input
              id="corrected-start"
              type="datetime-local"
              value={correctedAt}
              onChange={(e) => setCorrectedAt(e.target.value)}
            />
            <div className="flex gap-2">
              <Button disabled={!correctedAt || busy} onClick={runStartSession}>
                {busy ? "Starting…" : "Start with this time"}
              </Button>
              <Button variant="outline" onClick={back}>
                Back
              </Button>
            </div>
          </div>
        ) : null}

        {pane === "complete" ? (
          <div className="space-y-2">
            <p className="rounded-lg bg-muted p-2.5 text-xs leading-relaxed">
              Complete {booking.ref} now? This ends the session and starts the therapist&apos;s
              pickup
              {booking.payment_method === "cash"
                ? " — she then confirms the cash she received, which starts the cash handoff."
                : "."}
            </p>
            <div className="flex gap-2">
              <Button disabled={busy} onClick={runComplete}>
                {busy ? "Completing…" : "Confirm complete"}
              </Button>
              <Button variant="outline" onClick={back}>
                Back
              </Button>
            </div>
          </div>
        ) : null}

        {pane === "cancel" ? (
          <div className="space-y-2">
            {/* Says what happens to the money in words, never a figure: a number
                computed in the admin bundle is a second implementation of money
                math (money-math.md), and an operator would relay it to the
                client as a promise. The settled figure comes back from the
                server. */}
            <Callout tone="danger" role="alert">
              <p className="font-semibold text-destructive">
                Cancel {booking.ref}? This cannot be undone.
              </p>
              <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-muted-foreground">
                <li>
                  System cancel: no fee. Everything the client paid comes back as Credits, and Gift
                  Passes are restored.
                </li>
                <li>The therapist is freed.</li>
                <li>
                  Any open rider trip is closed — mileage up to the cancel point stays payable.
                </li>
              </ul>
              {booking.status === "in_session" ? (
                // The two buttons do opposite things to the client's money. Said
                // here, at the last moment it can still be the wrong button.
                <p className="mt-2 border-t border-destructive/20 pt-2 font-medium text-destructive">
                  This session is in progress. If the CLIENT asked to stop, go Back and use
                  &quot;Client asked to stop&quot; — this button refunds everything.
                </p>
              ) : null}
            </Callout>
            <Label htmlFor="cancel-reason">
              Reason <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Why is this booking being cancelled?"
            />
            <div className="flex gap-2">
              <Button
                variant="destructive"
                disabled={reason.trim().length === 0 || busy}
                onClick={runCancel}
              >
                {busy ? "Cancelling…" : "Confirm cancel"}
              </Button>
              <Button variant="outline" onClick={back}>
                Back
              </Button>
            </div>
          </div>
        ) : null}

        {pane === "transfer" ? (
          <RiderTransferPane
            booking={booking}
            riderId={riderId}
            onRiderId={setRiderId}
            onBack={back}
            onDone={() => {
              setPane("none");
              setRiderId("");
              onDone();
            }}
          />
        ) : null}

        {error ? (
          // The server's refusal, verbatim. The transition matrix, the hub scope
          // and the pre-pickup check all live there and this screen does not know
          // which rule was broken.
          <Callout tone="danger" role="alert">
            {error.message}
          </Callout>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Its own component so the dispatch-queue query is MOUNTED ONLY WHILE THE PANE
 * IS OPEN. `useRiderQueue` has no `enabled` guard, so calling it from the
 * parent would poll every 15s for the whole time the modal is merely open —
 * and with a null hubId that is an unfiltered read of every hub's queue.
 * Conditional rendering is the guard.
 */
function RiderTransferPane({
  booking,
  riderId,
  onRiderId,
  onBack,
  onDone,
}: {
  booking: Props["booking"];
  riderId: string;
  onRiderId: (id: string) => void;
  onBack: () => void;
  onDone: () => void;
}) {
  const assignRider = useAssignSpecificRider();
  const riderQueue = useRiderQueue(booking.hub_id ?? null);
  const error = assignRider.error as Error | null;

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Moves {booking.ref} to another rider in this hub. The current rider is returned to
        available.
      </p>
      <Label htmlFor="transfer-rider">Rider</Label>
      <NativeSelect
        id="transfer-rider"
        className="w-full"
        value={riderId}
        onChange={(e) => onRiderId(e.target.value)}
      >
        <NativeSelectOption value="">Select a rider…</NativeSelectOption>
        {(riderQueue.data ?? [])
          // Never offer the rider already on it — the server would bump them
          // back to available and then re-assign them to the same booking.
          .filter((row) => row.rider_id !== booking.rider_id)
          .map((row) => (
            <NativeSelectOption key={row.rider_id} value={row.rider_id}>
              {row.staff?.full_name ?? row.rider_id} · {row.status}
              {row.bookings?.ref ? ` · on ${row.bookings.ref}` : ""}
            </NativeSelectOption>
          ))}
      </NativeSelect>
      {riderQueue.isPending ? (
        <p className="text-xs text-muted-foreground">Loading riders…</p>
      ) : null}
      <div className="flex gap-2">
        <Button
          disabled={!riderId || assignRider.isPending}
          onClick={() =>
            assignRider.mutate({ bookingId: booking.id, riderId }, { onSuccess: onDone })
          }
        >
          {assignRider.isPending ? "Transferring…" : "Confirm transfer"}
        </Button>
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
      </div>
      {error ? (
        <Callout tone="danger" role="alert">
          {error.message}
        </Callout>
      ) : null}
    </div>
  );
}
